import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Lock } from 'lucide-react';

// Use same-origin API path on production custom domains
const API = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const FeaturesContext = createContext(null);

// Default features (all enabled) - used as fallback
const DEFAULT_FEATURES = {
  journal_entries: { enabled: true, limit: null },
  ai_coach: { enabled: true, limit: null },
  voice_typing: { enabled: true, limit: null },
  export_pdf: { enabled: true, limit: null },
  themes: { enabled: true, limit: null },
  dark_mode: { enabled: true, limit: null },
  mood_tracking: { enabled: true, limit: null },
  image_attachments: { enabled: true, limit: null },
  streaks: { enabled: true, limit: null },
  badges: { enabled: true, limit: null },
  link_preview: { enabled: true, limit: null },
  gamification: { enabled: true, limit: null },
};

export const FeaturesProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isB2BUser, setIsB2BUser] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [customMood, setCustomMood] = useState(null); // Custom branded mood for B2B clients

  // Allow setting preview features externally (for admin previewing B2B client)
  const setPreviewFeatures = useCallback((clientFeatures) => {
    if (!clientFeatures) {
      // Clear preview mode
      setIsPreviewMode(false);
      return;
    }
    
    // Convert B2B features format to our format
    const b2bFeatures = {};
    Object.entries(clientFeatures).forEach(([key, enabled]) => {
      // Map backend key names to frontend key names
      let mappedKey = key;
      if (key === 'pdf_export') mappedKey = 'export_pdf';
      if (key === 'image_attachments') mappedKey = 'image_attachments';
      if (key === 'photo_attachments') mappedKey = 'image_attachments';
      
      b2bFeatures[mappedKey] = { enabled, limit: enabled ? null : 0 };
    });
    
    setFeatures({ ...DEFAULT_FEATURES, ...b2bFeatures });
    setIsB2BUser(true);
    setIsPreviewMode(true);
    setTierInfo({
      source: 'b2b_preview',
      name: 'B2B Preview',
      client_name: 'Preview Client'
    });
    setLoading(false);
  }, []);

  // Fetch user's tier and features
  const fetchFeatures = useCallback(async () => {
    // Wait for auth to finish loading - but set a timeout to prevent infinite loading
    if (authLoading) {
      // If auth is still loading after this function is called,
      // we'll be called again when authLoading changes via the useEffect
      return;
    }
    
    // Check if user is logged in
    const isLoggedIn = !!user;
    
    if (!isLoggedIn) {
      setFeatures(DEFAULT_FEATURES);
      setIsB2BUser(false);
      setLoading(false);
      return;
    }

    try {
      // First check if user is part of a B2B client
      const clientConfig = await axios.get(`${API}/api/user/client-config`);
      
      if (clientConfig.data.has_client && clientConfig.data.client?.features) {
        // B2B USER DETECTED - mark as B2B so we never show upgrade prompts
        setIsB2BUser(true);
        
        // B2B-specific defaults: features that control user UI options should default to disabled
        // unless explicitly enabled by the B2B admin
        const B2B_FEATURE_DEFAULTS = {
          dark_mode: { enabled: false, limit: 0 },  // Default OFF - admin must enable
          themes: { enabled: false, limit: 0 },      // Default OFF - admin must enable
          // Other features default to enabled for B2B (they get full access)
          voice_typing: { enabled: true, limit: null },
          ai_coach: { enabled: true, limit: null },
          export_pdf: { enabled: true, limit: null },
          image_attachments: { enabled: true, limit: null },
          journal_entries: { enabled: true, limit: null },
          mood_tracking: { enabled: true, limit: null },
          popup_notifications: { enabled: true, limit: null },
          promo_banners: { enabled: true, limit: null },
        };
        
        // Convert B2B features format to our format
        const b2bFeatures = {};
        Object.entries(clientConfig.data.client.features).forEach(([key, enabled]) => {
          // Map backend key names to frontend key names
          let mappedKey = key;
          if (key === 'pdf_export') mappedKey = 'export_pdf';
          if (key === 'image_attachments') mappedKey = 'image_attachments';
          if (key === 'photo_attachments') mappedKey = 'image_attachments';
          
          b2bFeatures[mappedKey] = { enabled, limit: enabled ? null : 0 };
        });
        // Merge: DEFAULT_FEATURES as base, then B2B defaults, then actual client features
        setFeatures({ ...DEFAULT_FEATURES, ...B2B_FEATURE_DEFAULTS, ...b2bFeatures });
        
        // Check for custom branded mood
        const client = clientConfig.data.client;
        if (client.custom_mood_enabled && client.custom_mood_image_url && client.custom_mood_label) {
          setCustomMood({
            enabled: true,
            imageUrl: client.custom_mood_image_url,
            pdfImageUrl: client.custom_mood_pdf_image_url || null,  // Server-hosted image for PDFs
            label: client.custom_mood_label
          });
        } else {
          setCustomMood(null);
        }
        
        setTierInfo({
          source: 'b2b',
          clientName: clientConfig.data.client.name,
          appName: clientConfig.data.client.app_name
        });
        setLoading(false);
        return;
      }

      // Not a B2B user
      setIsB2BUser(false);
      setCustomMood(null);
      
      // Otherwise, get user's tier features
      const tierKey = user.membership_tier || 'free';
      const tiersResponse = await axios.get(`${API}/api/tiers`);
      const tiers = tiersResponse.data.tiers || [];
      const userTier = tiers.find(t => t.key === tierKey);

      if (userTier && userTier.features) {
        const tierFeatures = {};
        userTier.features.forEach(f => {
          tierFeatures[f.key] = { enabled: f.enabled, limit: f.limit };
        });
        setFeatures({ ...DEFAULT_FEATURES, ...tierFeatures });
        setTierInfo({
          source: 'tier',
          tier: userTier
        });
      } else {
        setFeatures(DEFAULT_FEATURES);
      }
    } catch (error) {
      // Silently fail - features default to enabled for better UX
      console.debug('Features fetch failed:', error.message);
      setFeatures(DEFAULT_FEATURES);
    } finally {
      setLoading(false);
    }
  }, [authLoading, user]);

  useEffect(() => {
    // Small delay to reduce initial server load
    const timer = setTimeout(() => {
      fetchFeatures();
    }, 800);
    
    // Safety timeout: if loading is still true after 5 seconds, force defaults
    const safetyTimer = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          console.debug('Features loading timed out, using defaults');
          setFeatures(DEFAULT_FEATURES);
          return false;
        }
        return prev;
      });
    }, 5000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(safetyTimer);
    };
  }, [fetchFeatures]);

  // Check if a specific feature is enabled
  const isFeatureEnabled = useCallback((featureKey) => {
    const feature = features[featureKey];
    if (!feature) return true; // Unknown features default to enabled
    return feature.enabled && feature.limit !== 0;
  }, [features]);

  // Get feature limit (null = unlimited, 0 = disabled, >0 = limited)
  const getFeatureLimit = useCallback((featureKey) => {
    const feature = features[featureKey];
    if (!feature) return null;
    return feature.limit;
  }, [features]);

  // Check if user can use a feature (considering limits)
  const canUseFeature = useCallback((featureKey, currentUsage = 0) => {
    const feature = features[featureKey];
    if (!feature) return true;
    if (!feature.enabled) return false;
    if (feature.limit === null) return true; // Unlimited
    if (feature.limit === 0) return false; // Disabled
    return currentUsage < feature.limit;
  }, [features]);

  const contextValue = useMemo(() => ({
    features,
    tierInfo,
    loading,
    isB2BUser,
    customMood,
    isFeatureEnabled,
    getFeatureLimit,
    canUseFeature,
    refreshFeatures: fetchFeatures,
    setPreviewFeatures
  }), [features, tierInfo, loading, isB2BUser, customMood, isFeatureEnabled, getFeatureLimit, canUseFeature, fetchFeatures, setPreviewFeatures]);

  return (
    <FeaturesContext.Provider value={contextValue}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    // Return default values if outside provider (for safety)
    return {
      features: DEFAULT_FEATURES,
      tierInfo: null,
      loading: false,
      isB2BUser: false,
      customMood: null,
      isFeatureEnabled: () => true,
      getFeatureLimit: () => null,
      canUseFeature: () => true,
      refreshFeatures: () => {},
      setPreviewFeatures: () => {}
    };
  }
  return context;
};

// HOC for feature-gated components
export const withFeatureGate = (WrappedComponent, featureKey, fallback = null) => {
  return function FeatureGatedComponent(props) {
    const { isFeatureEnabled, loading } = useFeatures();
    
    if (loading) return null;
    if (!isFeatureEnabled(featureKey)) return fallback;
    
    return <WrappedComponent {...props} />;
  };
};

// Component for inline feature gating
// mode: 'hide' (default) - hides children when disabled
// mode: 'prompt' - shows children but intercepts clicks and triggers onUpgrade
// NOTE: B2B users ALWAYS get 'hide' mode - they should never see upgrade prompts
export const FeatureGate = ({ 
  feature, 
  fallback = null, 
  children, 
  mode = 'hide',
  onUpgrade = null,
  showLockBadge = false 
}) => {
  const { isFeatureEnabled, loading, tierInfo, isB2BUser } = useFeatures();
  
  // While loading, show children (assume enabled) - this prevents UI flashing
  // Features will be properly gated once loaded
  // This is safe because DEFAULT_FEATURES enables everything
  if (loading) return children;
  
  const isEnabled = isFeatureEnabled(feature);
  
  // B2B users should NEVER see upgrade prompts - always hide disabled features
  const effectiveMode = isB2BUser ? 'hide' : mode;
  
  // Standard hide mode - original behavior
  if (effectiveMode === 'hide') {
    if (!isEnabled) return fallback;
    return children;
  }
  
  // Prompt mode - show content but intercept clicks (only for non-B2B users)
  if (effectiveMode === 'prompt') {
    if (isEnabled) return children;
    
    // Wrap children to intercept clicks and show upgrade prompt
    return (
      <div 
        className="relative cursor-pointer group"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onUpgrade) onUpgrade(feature);
        }}
      >
        <div className="pointer-events-none opacity-70">
          {children}
        </div>
        {/* Lock badge overlay */}
        {showLockBadge && (
          <div 
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: 'var(--theme-button-bg, linear-gradient(135deg, #df1283, #FF7901))' }}
          >
            <Lock className="w-3 h-3 text-white" />
          </div>
        )}
        {/* Hover tooltip */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-white text-xs font-medium px-3 py-1 rounded-full" style={{ background: 'var(--theme-button-bg, linear-gradient(135deg, #df1283, #FF7901))' }}>
            Upgrade to unlock
          </span>
        </div>
      </div>
    );
  }
  
  return children;
};

export default FeaturesContext;
